/* Leads (docs/MARKETING-HUB.md, Phase 0 — 2026-11-04): the homepage invite request and the /teams form land in the database
   through the anon `lead_add` RPC (honeypot, rate-capped, email checked, a 24h merge), which emails the owner at once through
   lead-notify. The hidden Netlify forms in index.html stay one release as the FALLBACK: when the RPC cannot be reached
   (a network blip, a stale deploy), the same fields post to Netlify as before, so no lead is lost either way. */
import { supabase, supabaseConfigured } from "./supabase";
import { deviceType } from "@/lib/siteTrack";
import { viewerId } from "@/lib/track";
import { touchForServer } from "./touch";

export type LeadKind = "invite_request" | "teams";
export interface LeadFields { name: string; email: string; occasion?: string; note?: string; company?: string; when?: string; headcount?: string; hp?: string }
export interface LeadAdded { ok: true; id?: string; already?: boolean; fallback?: boolean }

/* The RPC payload: the fields, the viewer/device/path, the stored touch pair, the honeypot. Pure. */
export function leadPayload(kind: LeadKind, f: LeadFields, ctx: { viewer: string; device: string; path: string; ua: string; touch: unknown }): Record<string, unknown> {
  return { kind, name: String(f.name || "").trim(), email: String(f.email || "").trim(), occasion: String(f.occasion || "").trim(), note: String(f.note || "").trim(),
    company: String(f.company || "").trim(), when: String(f.when || "").trim(), headcount: String(f.headcount || "").trim(), hp: String(f.hp || ""),
    viewer: ctx.viewer, device: ctx.device, path: ctx.path, ua: ctx.ua.slice(0, 200), touch: ctx.touch || {} };
}
/* The Netlify fallback body (url-encoded), exactly what the forms posted before 0144. Pure. */
export function netlifyBody(kind: LeadKind, f: LeadFields): string {
  const o: Record<string, string> = kind === "teams"
    ? { "form-name": "teams", "bot-field": f.hp || "", name: f.name, email: f.email, company: f.company || "", occasion: f.occasion || "", when: f.when || "", headcount: f.headcount || "", note: f.note || "" }
    : { "form-name": "beta", "bot-field": f.hp || "", name: f.name, email: f.email, occasion: f.occasion || "", note: f.note || "" };
  return Object.keys(o).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(o[k])).join("&");
}

/* Database first; Netlify when the database cannot be reached. A validation message from the server (a bad email, the
   hourly cap) is thrown as it is — that is an answer, not an outage. */
export async function addLead(kind: LeadKind, f: LeadFields): Promise<LeadAdded> {
  if (supabaseConfigured) {
    try {
      const p = leadPayload(kind, f, { viewer: viewerId(), device: deviceType(), path: location.pathname, ua: typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "", touch: touchForServer() });
      const r = await supabase().rpc("lead_add", { p });
      if (!r.error) return (r.data || { ok: true }) as LeadAdded;
      if (isCalmRefusal(r.error.message)) throw new Error(r.error.message);
      console.warn("lead_add: " + r.error.message);   // anything else (an old backend, a network error) → the Netlify fallback below
    } catch (e) { if (isCalmRefusal(String((e as Error).message || ""))) throw e; }
  }
  const r = await fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: netlifyBody(kind, f) });
  if (!r.ok) throw new Error("That didn't go through.");
  return { ok: true, fallback: true };
}
/* The server's own refusals are worded for the person; they are shown, not retried elsewhere. Pure. */
export const isCalmRefusal = (m: string): boolean => /valid email|your name|plenty for now|Nothing to send/i.test(m);
